import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { loginUser, clearErrors } from "../slices/userSlice";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, loading, error } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/my-business");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) {
      dispatch(clearErrors());
    }
  }, [dispatch, error]);

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(loginUser(formData)).unwrap();
      navigate("/my-business");
    } catch (err) {
      // Error is handled by the slice with toast
    }
  };

  return (
    <div style={{
      ...styles.container,
      padding: isMobile ? "16px" : "20px",
    }}>
      <div style={{
        ...styles.card,
        padding: isMobile ? "24px 16px" : "40px",
        maxWidth: isMobile ? "100%" : "400px",
      }}>
        <h1 style={{
          ...styles.title,
          fontSize: isMobile ? "24px" : "28px",
        }}>Login</h1>
        <p style={styles.subtitle}>Welcome back! Please login to your account.</p>

        <form onSubmit={handleSubmit} style={{
          ...styles.form,
          gap: isMobile ? "16px" : "20px",
        }}>
          <div style={styles.formGroup}>
            <label htmlFor="email" style={styles.label}>
              Email Address <span style={styles.required}>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                ...styles.input,
                borderColor: validationErrors.email ? "#dc3545" : "#e9edef",
                width: "100%",
              }}
              placeholder="Enter your email"
              disabled={loading}
            />
            {validationErrors.email && (
              <span style={styles.errorText}>{validationErrors.email}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="password" style={styles.label}>
              Password <span style={styles.required}>*</span>
            </label>
            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...styles.passwordInput,
                  borderColor: validationErrors.password ? "#dc3545" : "#e9edef",
                  width: "100%",
                }}
                placeholder="Enter your password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.passwordToggle}
                disabled={loading}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {validationErrors.password && (
              <span style={styles.errorText}>{validationErrors.password}</span>
            )}
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              padding: isMobile ? "12px 20px" : "12px 24px",
            }}
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <div style={{
          ...styles.footer,
          marginTop: isMobile ? "16px" : "20px",
        }}>
          <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#667781" }}>
            Don't have an account?{" "}
            <Link to="/register" style={styles.link}>
              Register here
            </Link>
          </p>
          <Link to="/forgot-password" style={styles.link}>
            Forgot Password?
          </Link>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f2f5",
    padding: "20px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    padding: "40px",
    width: "100%",
    maxWidth: "400px",
    border: "1px solid #e9edef",
  },
  title: {
    fontSize: "28px",
    fontWeight: "500",
    marginBottom: "8px",
    color: "#111b21",
    textAlign: "center",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "14px",
    color: "#667781",
    marginBottom: "30px",
    textAlign: "center",
    fontWeight: "400",
    letterSpacing: "-0.01em",
  },
  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: "400",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    color: "#111b21",
    letterSpacing: "-0.01em",
    boxSizing: "border-box",
  },
  passwordContainer: {
    position: "relative",
    width: "100%",
  },
  passwordInput: {
    paddingRight: "70px",
  },
  passwordToggle: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: "#00a884",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "400",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "color 0.2s",
    letterSpacing: "-0.01em",
  },
  inputError: {
    borderColor: "#dc3545",
  },
  errorText: {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
    fontWeight: "400",
  },
  button: {
    width: "100%",
    padding: "12px 24px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#ffffff",
    backgroundColor: "#dc3545",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    marginTop: "10px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    letterSpacing: "-0.01em",
  },
  footer: {
    marginTop: "20px",
    textAlign: "center",
    fontSize: "14px",
    paddingTop: "20px",
    borderTop: "1px solid #e9edef",
  },
  link: {
    color: "#00a884",
    textDecoration: "none",
    fontWeight: "400",
    letterSpacing: "-0.01em",
  },
};

export default Login;

